import React, { useState, useEffect } from "react";
import { Modal, Button, Typography, Space, Card, Tag, Badge, Row, Col, Statistic } from "antd";
import {
    RadarChartOutlined,
    ThunderboltOutlined,
    FileProtectOutlined,
    ArrowRightOutlined,
    ArrowLeftOutlined,
    BankOutlined,
    LineChartOutlined,
    SafetyCertificateOutlined,
    RocketOutlined,
    WalletOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    PlusOutlined,
    SearchOutlined,
    FilterOutlined,
    CalendarOutlined
} from "@ant-design/icons";
import { Landmark, TrendingUp, TrendingDown, HelpCircle, PieChart as PieIcon, Layout, FileText, DownloadOutlined, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import BankIcon from 'react-br-bank-icons';

const { Title, Text } = Typography;


const variants = {
    enter: (direction) => ({
        x: direction > 0 ? '100%' : '-100%',
        opacity: 0,
        scale: 0.95
    }),
    center: {
        zIndex: 1,
        x: 0,
        opacity: 1,
        scale: 1
    },
    exit: (direction) => ({
        zIndex: 0,
        x: direction < 0 ? '100%' : '-100%',
        opacity: 0,
        scale: 0.95
    })
};

export default function WhatsNewModal({ open, onClose }) {
    const [[page, direction], setPage] = useState([0, 0]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const slides = [
        {
            icon: <RocketOutlined style={{ color: '#005A9E' }} />,
            title: "A Nova Era da Gestão",
            subtitle: "Uma Experiência Totalmente Redesenhada",
            description: "Mais que uma atualização, uma revolução na forma como você cuida das finanças da sua empresa. Simples, rápido e inteligente.",
            color: "#eff6ff",
            preview: (
                <div style={{ width: '100%', height: '100%' }}>
                    <div style={{ background: '#f8fafc', borderRadius: '20px', height: '100%', border: '1px solid #e2e8f0', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {/* Header Mockup */}
                        <div style={{ padding: '12px 15px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <div style={{ width: 80, height: 12, background: '#f1f5f9', borderRadius: 6 }} />
                                <div style={{ width: 40, height: 12, background: '#f1f5f9', borderRadius: 6 }} />
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <div style={{ width: 24, height: 24, borderRadius: 6, background: '#f1f5f9' }} />
                                <div style={{ width: 60, height: 24, borderRadius: 6, background: '#005A9E', opacity: 0.8 }} />
                            </div>
                        </div>
                        {/* Content Mockup */}
                        <div style={{ padding: '15px', flex: 1 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 15 }}>
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} style={{ height: 45, background: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', padding: 8 }}>
                                        <div style={{ width: '40%', height: 6, background: '#f1f5f9', borderRadius: 3, marginBottom: 6 }} />
                                        <div style={{ width: '70%', height: 10, background: '#0f172a', opacity: 0.1, borderRadius: 3 }} />
                                    </div>
                                ))}
                            </div>
                            <div style={{ height: '100px', background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                    <div style={{ width: '30%', height: 10, background: '#f1f5f9', borderRadius: 5 }} />
                                    <div style={{ width: '10%', height: 10, background: '#f1f5f9', borderRadius: 5 }} />
                                </div>
                                {[1, 2, 3].map(i => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <div style={{ width: '60%', height: 6, background: '#f8fafc', borderRadius: 3 }} />
                                        <div style={{ width: '15%', height: 6, background: '#f8fafc', borderRadius: 3 }} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            icon: <TrendingUp style={{ color: '#059669' }} />,
            title: "Inteligência Financeira",
            subtitle: "Controle Absoluto de Caixa",
            description: "Visualize seu saldo anterior, entradas e saídas do mês em tempo real, com clareza e precisão matemática.",
            color: "#ecfdf5",
            preview: (
                <div style={{ width: '100%' }}>
                    <Row gutter={[12, 12]}>
                        <Col span={12}>
                            <Card size="small" style={{ borderRadius: 12, border: '1px solid #bbf7d0', background: '#ffffff' }}>
                                <Statistic
                                    title={<Text style={{ fontSize: 10, color: '#64748b' }}>CRÉDITOS</Text>}
                                    value={18450.00}
                                    precision={2}
                                    valueStyle={{ color: '#059669', fontSize: 16, fontWeight: 900 }}
                                    prefix={<ArrowUpOutlined style={{ fontSize: 14 }} />}
                                />
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" style={{ borderRadius: 12, border: '1px solid #fecaca', background: '#ffffff' }}>
                                <Statistic
                                    title={<Text style={{ fontSize: 10, color: '#64748b' }}>DÉBITOS</Text>}
                                    value={12320.50}
                                    precision={2}
                                    valueStyle={{ color: '#ef4444', fontSize: 16, fontWeight: 900 }}
                                    prefix={<ArrowDownOutlined style={{ fontSize: 14 }} />}
                                />
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card size="small" style={{ borderRadius: 12, border: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 10, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4f46e5' }}>
                                        <WalletOutlined style={{ fontSize: 20 }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>SALDO ATUAL</div>
                                        <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>R$ 6.129,50</div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            icon: <FileText style={{ color: '#005A9E' }} size={32} />,
            title: "Conciliação Ágil",
            subtitle: "Gestão Transparente de Movimentos",
            description: "Filtre seus lançamentos por tipo, status ou período. Identifique pendências e anexe documentos com um clique.",
            color: "#f8fafc",
            preview: (
                <div style={{ width: '100%' }}>
                    <div style={{ background: '#ffffff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
                        <div style={{ background: '#f8fafc', padding: '10px 15px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8 }}>
                            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', fontSize: 9, fontWeight: 700 }}>TIPO</div>
                            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px', fontSize: 9, fontWeight: 700 }}>STATUS</div>
                        </div>
                        <div style={{ padding: '0 15px' }}>
                            {[
                                { desc: 'Pagamento AWS Cloud', val: '- 145,00', type: 'D', status: 'W' },
                                { desc: 'Receita Consultoria TI', val: '+ 4.500,00', type: 'C', status: 'L' },
                                { desc: 'Aluguel Escritório', val: '- 2.100,00', type: 'D', status: 'L' }
                            ].map((item, i) => (
                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 2 ? '1px solid #f1f5f9' : 'none' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Text strong style={{ fontSize: 12, color: '#1e293b' }}>{item.desc}</Text>
                                        <Tag color={item.status === 'W' ? 'warning' : 'success'} style={{ fontSize: 8, margin: 0, borderRadius: 3, padding: '0 4px', lineHeight: '14px' }}>
                                            {item.status === 'W' ? 'AGUARDANDO' : 'LIQUIDADO'}
                                        </Tag>
                                    </div>
                                    <Text strong style={{ fontSize: 12, color: item.type === 'C' ? '#059669' : '#ef4444' }}>{item.val}</Text>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )
        },
        {
            icon: <BankOutlined style={{ color: '#7c3aed' }} />,
            title: "Conectividade Total",
            subtitle: "Open Finance & Integrações",
            description: "Sincronize suas contas bancárias diretamente. Menos digitação, mais precisão e total segurança de dados.",
            color: "#f5f3ff",
            preview: (
                <div style={{ width: '100%' }}>
                    <div style={{ background: '#ffffff', borderRadius: 20, padding: 12, border: '1px solid #e2e8f0', boxShadow: '0 15px 35px rgba(0,0,0,0.06)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobile ? 15 : 20 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#f5f3ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Landmark size={18} />
                                </div>
                                <Text strong style={{ fontSize: 14 }}>Contas Conectadas</Text>
                            </div>
                            <Button type="primary" size="small" shape="circle" icon={<PlusOutlined />} style={{ background: '#7c3aed', border: 'none' }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[
                                { name: 'Itaú Empresas', balance: 'R$ 12.450,00', bankId: '341' },
                                { name: 'Nubank Business', balance: 'R$ 5.120,50', bankId: '260' }
                            ].map((bank, i) => (
                                <motion.div
                                    initial={{ x: -10, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.5 + (i * 0.1) }}
                                    key={bank.name}
                                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: '#f8fafc', borderRadius: 12, border: '1px solid #f1f5f9' }}
                                >
                                    <div style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <BankIcon bankId={bank.bankId} size={28} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>{bank.name}</div>
                                        <div style={{ fontSize: 10, color: '#64748b' }}>{bank.balance}</div>
                                    </div>
                                    <Badge status="success" />
                                </motion.div>
                            ))}
                        </div>
                        <div style={{ marginTop: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 10, fontWeight: 700, color: '#10b981' }}>
                            <ShieldCheck size={12} /> Dados Criptografados AES-256
                        </div>
                    </div>
                </div>
            )
        },
        {
            icon: <PieIcon style={{ color: '#e11d48' }} size={32} />,
            title: "Visão de Custos",
            subtitle: "Decisões Baseadas em Dados",
            description: "Descubra para onde seu dinheiro está indo com dashboards intuitivos que categorizam suas despesas automaticamente.",
            color: "#fff1f2",
            preview: (
                <div style={{ width: '100%', perspective: '1200px' }}>
                    <motion.div
                        initial={{ rotateX: 5, rotateY: -5 }}
                        animate={{ rotateX: 0, rotateY: 0 }}
                        className="preview-card-shadow"
                    >
                        <Card style={{ borderRadius: 20, border: '1px solid #fecaca', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }} bodyStyle={{ padding: 18 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                <div style={{ width: 110, height: 110, position: 'relative' }}>
                                    <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                                        <circle cx="18" cy="18" r="15.915" fill="transparent" stroke="#f1f5f9" strokeWidth="4"></circle>
                                        <motion.circle
                                            initial={{ strokeDasharray: "0 100" }}
                                            animate={{ strokeDasharray: "70 100" }}
                                            transition={{ duration: 1.5, delay: 0.8 }}
                                            cx="18" cy="18" r="15.915" fill="transparent" stroke="#e11d48" strokeWidth="4"
                                        />
                                    </svg>
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                                        <div style={{ fontSize: 8, color: '#64748b', fontWeight: 800 }}>FIXO</div>
                                        <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>70%</div>
                                    </div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                            <Text style={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>Aluguel</Text>
                                            <Text style={{ fontSize: 11, fontWeight: 800, color: '#e11d48' }}>42%</Text>
                                        </div>
                                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                                            <motion.div initial={{ width: 0 }} animate={{ width: '42%' }} transition={{ delay: 1.2, duration: 1 }} style={{ height: '100%', background: 'linear-gradient(90deg, #e11d48, #fb7185)', borderRadius: 4 }} />
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                            <Text style={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>Energia</Text>
                                            <Text style={{ fontSize: 11, fontWeight: 800, color: '#f43f5e' }}>15%</Text>
                                        </div>
                                        <div style={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                                            <motion.div initial={{ width: 0 }} animate={{ width: '15%' }} transition={{ delay: 1.4, duration: 1 }} style={{ height: '100%', background: 'linear-gradient(90deg, #f43f5e, #fda4af)', borderRadius: 4 }} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                </div>
            )
        }
    ];

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const paginate = (newDirection) => {
        const nextPage = page + newDirection;
        if (nextPage >= 0 && nextPage < slides.length) {
            setPage([nextPage, newDirection]);
        }
    };

    const isLastSlide = page === slides.length - 1;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={isMobile ? '100%' : 850}
            className="whats-new-modal"
            bodyStyle={{ padding: 0 }}
            closeIcon={null}
            style={{ top: isMobile ? 10 : undefined, paddingBottom: isMobile ? 10 : 0 }}
        >
            <div style={{
                height: isMobile ? 'auto' : '700px',
                minHeight: isMobile ? '50vh' : 'unset',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: isMobile ? '24px' : '32px',
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Progress Bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: '#f8fafc', zIndex: 100 }}>
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${((page + 1) / slides.length) * 100}%` }}
                        style={{ height: '100%', background: 'linear-gradient(90deg, #005A9E, #6366f1)', borderRadius: '0 4px 4px 0' }}
                    />
                </div>

                {/* Close Button X */}
                <Button
                    type="text"
                    icon={<PlusOutlined style={{ transform: 'rotate(45deg)', fontSize: 20 }} />}
                    onClick={onClose}
                    style={{ position: 'absolute', top: 15, right: 15, zIndex: 101, color: '#94a3b8' }}
                />

                <div style={{ flex: 1, position: 'relative', minHeight: isMobile ? '500px' : 'unset' }}>
                    <AnimatePresence initial={false} custom={direction}>
                        <motion.div
                            key={page}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: "spring", stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 }
                            }}
                            style={{
                                position: isMobile ? 'relative' : 'absolute',
                                width: '100%',
                                height: isMobile ? 'auto' : '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isMobile ? '50px 20px 30px' : '60px 80px',
                                boxSizing: 'border-box'
                            }}
                        >
                            {/* Decorative Background Blob */}
                            <motion.div
                                animate={{
                                    scale: [1, 1.2, 1],
                                    rotate: [0, 90, 0],
                                    opacity: [0.6, 1, 0.6]
                                }}
                                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                style={{
                                    position: 'absolute',
                                    width: isMobile ? '300px' : '550px',
                                    height: isMobile ? '300px' : '550px',
                                    background: slides[page].color,
                                    filter: 'blur(80px)',
                                    zIndex: -1,
                                    borderRadius: '50%',
                                    top: isMobile ? '10%' : '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none'
                                }}
                            />

                            {/* visual UI Preview */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3 }}
                                style={{
                                    width: '100%',
                                    maxWidth: isMobile ? '280px' : '550px',
                                    height: isMobile ? '180px' : '320px',
                                    marginBottom: isMobile ? '24px' : '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    perspective: '1000px',
                                    flexShrink: 0
                                }}
                            >
                                <motion.div
                                    style={{ width: '100%', height: '100%' }}
                                    animate={{ rotateY: [-1, 1, -1] }}
                                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    {slides[page].preview}
                                </motion.div>
                            </motion.div>

                            {/* Text Content */}
                            <div style={{ textAlign: 'center', width: '100%', zIndex: 2 }}>
                                <motion.div
                                    initial="hidden"
                                    animate="visible"
                                    variants={{
                                        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.5 } }
                                    }}
                                >
                                    <motion.div
                                        variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                                        style={{
                                            display: 'flex',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: isMobile ? 12 : 20,
                                            marginBottom: isMobile ? 16 : 24
                                        }}
                                    >
                                        <div style={{ fontSize: isMobile ? '40px' : '52px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.1))', lineHeight: 1 }}>
                                            {slides[page].icon}
                                        </div>
                                        <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                                            <Text style={{
                                                color: '#005A9E',
                                                fontWeight: 900,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.2em',
                                                fontSize: isMobile ? '10px' : '11px',
                                                display: 'block',
                                                marginBottom: 4
                                            }}>
                                                {slides[page].title}
                                            </Text>
                                            <Title level={isMobile ? 4 : 2} style={{ margin: 0, fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', fontSize: isMobile ? '20px' : 'auto' }}>
                                                {slides[page].subtitle}
                                            </Title>
                                        </div>
                                    </motion.div>

                                    <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}>
                                        <Text style={{
                                            fontSize: isMobile ? '14px' : '18px',
                                            color: '#64748b',
                                            lineHeight: '1.6',
                                            display: 'block',
                                            maxWidth: isMobile ? '100%' : '550px',
                                            margin: '0 auto',
                                            fontWeight: 500,
                                            padding: isMobile ? '0 10px' : '0'
                                        }}>
                                            {slides[page].description}
                                        </Text>
                                    </motion.div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Footer Navigation */}
                <div style={{
                    padding: isMobile ? '20px 16px' : '30px 60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderTop: '1px solid #f1f5f9',
                    background: '#ffffff',
                    zIndex: 101,
                    borderRadius: isMobile ? '0 0 24px 24px' : '0 0 32px 32px',
                    marginTop: 'auto'
                }}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => paginate(-1)}
                        disabled={page === 0}
                        style={{ color: page === 0 ? '#cbd5e1' : '#64748b', fontWeight: 700, minWidth: isMobile ? 40 : 100 }}
                    >
                        {!isMobile && "Anterior"}
                    </Button>

                    <div style={{ display: 'flex', gap: 6, margin: '0 10px' }}>
                        {slides.map((_, i) => (
                            <motion.div
                                key={i}
                                animate={{
                                    width: i === page ? (isMobile ? 24 : 32) : 8,
                                    background: i === page ? '#005A9E' : '#e2e8f0'
                                }}
                                style={{ height: 8, borderRadius: 4, transition: 'all 0.3s' }}
                            />
                        ))}
                    </div>

                    {isLastSlide ? (
                        <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button
                                type="primary"
                                onClick={onClose}
                                style={{
                                    background: 'linear-gradient(135deg, #005A9E 0%, #4f46e5 100%)',
                                    border: 'none',
                                    borderRadius: 12,
                                    height: isMobile ? 42 : 48,
                                    padding: isMobile ? '0 16px' : '0 32px',
                                    fontWeight: 700,
                                    boxShadow: '0 10px 15px -3px rgba(0, 90, 158, 0.3)',
                                    fontSize: isMobile ? '13px' : '14px'
                                }}
                            >
                                {isMobile ? "Começar" : "Começar agora"}
                            </Button>
                        </motion.div>
                    ) : (
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            onClick={() => paginate(1)}
                            style={{
                                background: '#005A9E',
                                border: 'none',
                                borderRadius: 12,
                                height: isMobile ? 42 : 48,
                                padding: isMobile ? '0 16px' : '0 32px',
                                fontWeight: 700,
                                minWidth: isMobile ? 40 : 100,
                                fontSize: isMobile ? '13px' : '14px'
                            }}
                        >
                            {!isMobile && "Próximo"}
                        </Button>
                    )}
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .whats-new-modal .ant-modal-content {
                    border-radius: 32px !important;
                    overflow: visible !important;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
                    background: transparent !important;
                }
                .whats-new-modal .ant-modal-close {
                    display: none !important;
                }
                .preview-card-shadow {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
                }
            `}} />
        </Modal>
    );
}
